<template>
  <div class="feedbacks-topbar">
    <!-- busca de loja -->
    <div class="topbar-search">
      <b-autocomplete
        v-model="storeSearch"
        :data="filteredStores"
        field="name"
        placeholder="Buscar loja ou CNPJ..."
        clearable
        open-on-focus
        @select="onStoreSelect"
      >
        <template slot-scope="props">
          <div v-if="props.option.type === 'all'" class="option-all">
            <span class="material-icons option-all-icon">store</span>
            <span>{{ props.option.name }}</span>
          </div>
          <div v-else class="option-store">
            <span>{{ props.option.name }}</span>
            <span v-if="props.option.cnpj" class="option-cnpj-hint">{{
              formatCnpj(props.option.cnpj)
            }}</span>
          </div>
        </template>
        <template slot="empty">Nenhuma loja encontrada</template>
      </b-autocomplete>
    </div>

    <!-- ações: buscar + download -->
    <div class="topbar-actions">
      <button
        class="action-btn action-btn--primary"
        :disabled="!selectedStoreId"
        title="Buscar feedbacks"
        @click="$emit('get-feedbacks')"
      >
        <b-icon icon="search" size="is-small" />
      </button>
      <button
        class="action-btn"
        :disabled="!selectedStoreId"
        title="Baixar relatório"
        @click="$emit('download-feedbacks-report')"
      >
        <b-icon icon="download" size="is-small" />
      </button>
    </div>

    <!-- campo de data estilizado -->
    <b-datepicker
      v-model="dates"
      range
      :mobile-native="false"
      position="is-bottom-left"
      @input="$emit('update-dates', dates)"
    >
      <template #trigger>
        <div
          class="date-trigger"
          :class="{ 'date-trigger--active': dates && dates[0] }"
        >
          <span class="date-trigger-text">
            <template v-if="dates && dates[0]">
              {{ formatDate(dates[0]) }}
              <span class="date-arrow">›</span>
              {{ formatDate(dates[1]) }}
            </template>
            <template v-else> dd/mm/aa › dd/mm/aa </template>
          </span>
          <span class="date-trigger-icon">
            <b-icon icon="calendar-alt" size="is-small" />
          </span>
        </div>
      </template>
    </b-datepicker>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";

export default {
  mixins: [common],

  data() {
    return {
      dates: null,
      storeSearch: "",
      selectedStoreId: null,
      selectedStoreName: null
    };
  },

  computed: {
    ...mapGetters(["merchants"]),

    filteredStores() {
      const q = this.storeSearch.toLowerCase().trim();
      const allOption = { type: "all", name: "Todas as lojas" };

      const stores = this.merchants
        .flatMap(m =>
          (m.stores || []).map(s => ({
            type: "store",
            name: s.storeInfo.name,
            cnpj: (m.companyInfo && m.companyInfo.nationalCompanyId) || "",
            storeId: s._id
          }))
        )
        .sort((a, b) => a.name.localeCompare(b.name));

      if (!q || "todas as lojas".includes(q)) {
        return [allOption, ...stores];
      }

      return stores.filter(
        s =>
          s.name.toLowerCase().includes(q) ||
          this.formatCnpj(s.cnpj).toLowerCase().includes(q)
      );
    }
  },

  methods: {
    formatCnpj(cnpj) {
      if (!cnpj) return "";
      const c = cnpj.replace(/\D/g, "");
      if (c.length !== 14) return cnpj;
      return c.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5"
      );
    },

    formatDate(d) {
      if (!d) return "";
      const date = new Date(d);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    },

    onStoreSelect(option) {
      if (!option) {
        this.selectedStoreId = null;
        this.selectedStoreName = null;
        this.$emit("update-store", null);
      } else if (option.type === "all") {
        this.selectedStoreId = "all";
        this.selectedStoreName = option.name;
        this.$emit("update-store", null);
      } else {
        this.selectedStoreId = option.storeId;
        this.selectedStoreName = option.name;
        this.$emit("update-store", {
          storeId: option.storeId,
          storeName: option.name
        });
      }
    }
  },

  mounted() {
    this.dates = [new Date(), new Date()];
  }
};
</script>

<style scoped>
/* ── barra principal ── */
.feedbacks-topbar {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

/* ── busca de loja (cresce) ── */
.topbar-search {
  flex: 1 1 220px;
  min-width: 0;
}

.topbar-search ::v-deep .input {
  border-radius: 8px;
  border-color: #e0e0e0;
  font-size: 0.875rem;
  height: 38px;
}

/* ── botões de ação ── */
.topbar-actions {
  display: flex;
  gap: 0.35rem;
  flex-shrink: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  color: #555;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  outline: none;
}

.action-btn:hover:not(:disabled) {
  background: #fff4ee;
  border-color: #ff7034;
  color: #ff7034;
}

.action-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.action-btn--primary {
  background: linear-gradient(135deg, #ff924c, #ff5c1a);
  border-color: transparent;
  color: #fff;
}

.action-btn--primary:hover:not(:disabled) {
  opacity: 0.9;
  background: linear-gradient(135deg, #ff924c, #ff5c1a);
  border-color: transparent;
  color: #fff;
}

/* ── campo de data customizado ── */
.date-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 38px;
  padding: 0 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.15s;
  user-select: none;
}

.date-trigger:hover {
  border-color: #ff7034;
}

.date-trigger--active {
  border-color: #e0e0e0;
}

.date-trigger-text {
  font-size: 0.82rem;
  color: #444;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.date-arrow {
  color: #ff7034;
  font-size: 1rem;
  line-height: 1;
}

.date-trigger-icon {
  color: #ff7034;
  display: flex;
  align-items: center;
}

.date-trigger-icon ::v-deep .icon {
  margin: 0 !important;
}

/* ── opções do autocomplete ── */
.option-all {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  color: #ff5c1a;
  padding: 2px 0;
}

.option-all-icon {
  font-size: 1rem;
}

.option-store {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.option-cnpj-hint {
  font-size: 0.8rem;
  color: hsl(0, 0%, 55%);
}

/* ── responsividade ── */
@media (max-width: 600px) {
  .topbar-search {
    flex: 1 1 100%;
  }
  .topbar-actions {
    flex-shrink: 0;
  }
}
</style>
